import { FunctionComponent, useMemo } from 'react'

import {
  ProviderDrawerSection,
  ProviderDrawer,
  ProviderInfoSection,
  ShareLinkButton,
  SpecialistRating
} from '@/modules/accredited-establishments/components'

import { SPECIALIST_DRAWER_ID } from './SpecialistDetailDrawer.constants'
import { WParagraph, WTag } from '@alice-health/wonderland-react'
import {
  buildShareUrl,
  getAppointmentLabel,
  getProviderDetailImage,
  getSpecialistTags,
  toLines,
  toList
} from '@/modules/accredited-establishments/functions'
import { useGetSpecialistDetail } from '@/modules/accredited-establishments/hooks/useGetSpecialistDetail'

interface SpecialistDetailDrawerProps {
  referencedId?: string
}

const SpecialistDetailDrawer: FunctionComponent<
  SpecialistDetailDrawerProps
> = ({ referencedId }) => {
  const { data, isLoading, isError } = useGetSpecialistDetail({
    referencedId
  })

  const details = useMemo(() => {
    if (!data) return undefined

    return {
      title: data.name,
      image: getProviderDetailImage(data.photo, 'SPECIALIST'),
      tags: getSpecialistTags(data)
    }
  }, [data])

  const education = toLines(data?.education)
  const qualifications = toLines(data?.qualifications)

  const appointmentLabel = data ? getAppointmentLabel(data) : undefined

  /*
   * O handoff troca o texto institucional pelo comentario consolidado por IA.
   * `about` fica como reserva: sem `rating` — rede Cassi, comunidade clinica ou
   * menos de 10 avaliacoes — o drawer ficaria sem nenhum texto de apresentacao.
   */
  const about = data?.rating?.aiGeneratedComment ? undefined : data?.about

  return (
    <ProviderDrawer
      id={SPECIALIST_DRAWER_ID}
      kind='specialist'
      loading={isLoading}
      error={isError}
      data={details}
      actions={
        /*
         * Enquanto nao existe rota propria do especialista (HC-2641), o link
         * compartilhavel e a listagem com o `referencedId` na query. O
         * `getUrl` so roda no clique: resolver a URL cedo (num useMemo
         * travado so em `referencedId`) corre o risco de capturar o endereco
         * de antes do selamento assincrono (Web Crypto) terminar.
         */
        referencedId ? (
          <ShareLinkButton
            getUrl={() => buildShareUrl(referencedId)}
            title={details?.title}
          />
        ) : null
      }
    >
      {data?.rating ? <SpecialistRating rating={data.rating} /> : null}

      {about ? (
        <ProviderDrawerSection title='Sobre mim'>
          {toLines(about).map(line => (
            <WParagraph
              key={line}
              mode='tertiary'
            >
              {line}
            </WParagraph>
          ))}
        </ProviderDrawerSection>
      ) : null}

      {data?.includedPlans?.length ? (
        <ProviderDrawerSection title='Planos'>
          <WParagraph mode='tertiary'>{toList(data.includedPlans)}</WParagraph>
        </ProviderDrawerSection>
      ) : null}

      {data?.subSpecialties?.length ? (
        <ProviderDrawerSection title='Subespecialidades'>
          <WParagraph mode='tertiary'>{toList(data.subSpecialties)}</WParagraph>
        </ProviderDrawerSection>
      ) : null}

      {education.length ? (
        <ProviderDrawerSection title='Formação'>
          {education.map(line => (
            <WParagraph
              key={line}
              mode='tertiary'
            >
              {line}
            </WParagraph>
          ))}
        </ProviderDrawerSection>
      ) : null}

      <ProviderDrawerSection
        title='Atendimento'
        action={
          appointmentLabel ? (
            <WTag
              className='tag-pill-theme'
              label={appointmentLabel}
              color='gray'
              size='small'
            />
          ) : null
        }
      >
        <ProviderInfoSection
          remote={data?.remote}
          address={data?.address?.map(item => ({ address: item }))}
          phones={data?.phones}
          isCassi={data?.contractOrigin === 'CASSI'}
        />
      </ProviderDrawerSection>

      {qualifications.length ? (
        <ProviderDrawerSection title='Certificações e qualificações'>
          {qualifications.map(line => (
            <WParagraph
              key={line}
              mode='tertiary'
            >
              {line}
            </WParagraph>
          ))}
        </ProviderDrawerSection>
      ) : null}
    </ProviderDrawer>
  )
}

export type { SpecialistDetailDrawerProps }

export { SpecialistDetailDrawer }
